'use client';

import { CaretUp, LocationArrow } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';
import { useContext, useEffect, useRef, useState } from 'react';
import { haversineDistance } from 'utils/haversine';
import { Container } from '..';
import {
  FindOnGlobeContext,
  ScreenState,
  SEARCH_INPUT_MIN_CHAR_LENGTH,
  StoreElementType,
} from './context';
import { H1 } from './styleConstants';
import styles from './FinderControlsUI.module.css';

const UserLocationFlowButton = () => {
  const FGC = useContext(FindOnGlobeContext);

  const handleClick = async () => {
    FGC.populateUserLocationStores();
    FGC.setScreenState(ScreenState.SEARCH);
  };

  return (
    <button className={styles.userLocationFlowButton} onClick={handleClick}>
      <LocationArrow fill="#ffffff" /> Use my current location
    </button>
  );
};

const LandingScreenContent = () => {
  const FGC = useContext(FindOnGlobeContext);
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  // Effect to trigger search when query changes with debounce
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (
      localSearchQuery &&
      localSearchQuery.length >= SEARCH_INPUT_MIN_CHAR_LENGTH
    ) {
      timeoutId = setTimeout(() => {
        FGC.search(localSearchQuery);
        FGC.setScreenState(ScreenState.SEARCH);
      }, 500);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [localSearchQuery, FGC]);

  return (
    <Container className={styles.landingRoot}>
      <H1>
        Find your closest
        <br />
        Ultrahuman partner store
      </H1>

      <div className={`${styles.searchContainer} ${styles.pointerEvents}`}>
        <div className={styles.searchBarWrapper}>
          <form
            action=""
            onSubmit={(e) => {
              e.preventDefault();
              // The search is now handled by the debounced effect above
              // This form submission just prevents page reload
            }}
          >
            <div style={{ position: 'relative' }}>
              <input
                className={styles.searchBarInput}
                name="search"
                placeholder="Search by location, city or store name."
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.form?.dispatchEvent(
                      new Event('submit', { cancelable: true, bubbles: true }),
                    );
                    if (window) {
                      window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                      });
                      e.currentTarget.blur();
                    }
                  }
                }}
              />
              {localSearchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setLocalSearchQuery('');
                    FGC.clearSearch();
                  }}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.6)',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </form>
          <div
            className="location-btn-container"
            style={{ textAlign: 'center', marginTop: '16px' }}
          >
            <UserLocationFlowButton />
          </div>
        </div>
      </div>
    </Container>
  );
};

const SearchResultElement = ({
  store,
  index,
  active = false,
}: {
  store: StoreElementType;
  index: number;
  active?: boolean;
}) => {
  const FGC = useContext(FindOnGlobeContext);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (active) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [active]);

  const isStoreOpenCurrently = (
    openTime: string,
    closeTime: string,
  ): boolean => {
    let op = true;
    const currentTime = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (currentTime < openTime || currentTime > closeTime) {
      op = false;
    }
    return op;
  };

  const distanceFromUser: number = (() => {
    if (!FGC.userLocation) return 0;
    return Number(
      haversineDistance(
        FGC.userLocation.coords.latitude,
        FGC.userLocation.coords.longitude,
        Number(store.lat),
        Number(store.long),
      ).toFixed(1),
    );
  })();

  return (
    <div
      ref={ref}
      className={`search-result-element ${styles.searchResult} ${
        active ? styles.active : ''
      }`}
      style={{ animationDelay: index > 15 ? `0s` : `${index * 0.05}s` }}
      aria-hidden="true"
      onClick={(e) => {
        e.stopPropagation();
        FGC.setSearchControlVisible(!FGC.searchControlVisible);
        FGC.setSelectedStore(store);
      }}
    >
      {FGC.userLocation &&
      index === 0 &&
      distanceFromUser < 150 &&
      FGC.userCountry?.country?.toUpperCase() === store.iso.toUpperCase() ? (
        <div className={styles.tag}>NEAREST</div>
      ) : null}
      <div className={styles.title}>
        <h3>{store.retailPartnerName}</h3>
        {store.iso.toUpperCase() === FGC.userCountry?.country?.toUpperCase() &&
        distanceFromUser !== 0 ? (
          <p className={styles.storeDistance}>
            {FGC.userLocation ? distanceFromUser + ' kms' : ''}
          </p>
        ) : null}
      </div>
      <div className={styles.address}>
        <p>
          {/* {store.city}, {store.country} */}
          {store.address}, {store.country}
        </p>
        {/* <p>
            {store.city}, {store.country}
          </p> */}
      </div>
      <hr />
      <div className={styles.statusCtaContainer}>
        {isStoreOpenCurrently(store.storeOpenTime, store.storeClosingTime) ? (
          <span className={styles.status} style={{ color: '#019C47' }}>
            Open <i>• Closes at {store.storeClosingTime}</i>
          </span>
        ) : (
          <span className={styles.status} style={{ color: '#F84A54' }}>
            Closed <i>• Opens at {store.storeOpenTime}</i>
          </span>
        )}

        {/* <span
            className="status"
            style={{ color: store.inStore ? '#019C47' : '#F84A54' }}
          >
            {isStoreOpenCurrently(store.storeOpenTime, store.storeClosingTime)
              ? 'Open'
              : 'Closed'}
          </span> */}

        <div className="cta-container">
          {store.gMapUrl ? (
            <CustomLink
              onClick={(e) => e.stopPropagation()}
              href={store.gMapUrl as string}
              target="_blank"
            >
              Get Directions
            </CustomLink>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const SearchScreenContent = () => {
  const FGC = useContext(FindOnGlobeContext);
  const [localSearchQuery, setLocalSearchQuery] = useState(
    FGC.searchQuery ?? '',
  );

  console.log(
    'SearchScreenContent render - screenState:',
    FGC.screenState,
    'resultType:',
    FGC.resultType,
    'searchResult length:',
    FGC.searchResult.length,
  );

  // Effect to trigger search when query changes with debounce
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (
      localSearchQuery &&
      localSearchQuery.length >= SEARCH_INPUT_MIN_CHAR_LENGTH
    ) {
      timeoutId = setTimeout(() => {
        FGC.search(localSearchQuery);
      }, 500);
    } else if (localSearchQuery === '') {
      // Clear search when input is empty
      FGC.clearSearch();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [localSearchQuery, FGC]);

  const getResultArray = () => {
    console.log(
      'getResultArray called with resultType:',
      FGC.resultType,
      'searchResult length:',
      FGC.searchResult.length,
    );
    switch (FGC.resultType) {
      case 'search':
        return FGC.searchResult;
      case 'cluster':
        return FGC.selectedCluster?.clusterPoints || [];
      case 'userLocation':
        return FGC.searchResult;
      default:
        return [];
    }
  };
  return (
    <div
      className={`${styles.searchScreenRoot} ${
        FGC.searchControlVisible ? styles.pointerEvents : ''
      }`}
      style={{
        zIndex: 2,
      }}
      aria-hidden="true"
      onClick={(e) => {
        e.stopPropagation();
        FGC.setSearchControlVisible(false);
      }}
    >
      <div
        aria-hidden="true"
        className={`${styles.contentContainer} ${styles.pointerEvents} u-hide-scrollbar ${
          FGC.searchControlVisible ? styles.smVisible : ''
        }`}
        onClick={(e) => {
          e.stopPropagation();
          if (!FGC.searchControlVisible) FGC.setSearchControlVisible(true);
        }}
      >
        <button
          className={styles.visibilityToggleBtn}
          onClick={(e) => {
            e.stopPropagation();
            FGC.setSearchControlVisible(!FGC.searchControlVisible);
          }}
        >
          {FGC.searchControlVisible ? (
            <CaretUp style={{ transform: 'rotate(180deg)' }} />
          ) : (
            <CaretUp />
          )}
        </button>
        <div
          className={styles.searchContentWrapper}
          style={{
            pointerEvents: FGC.searchControlVisible ? 'all' : 'none',
          }}
        >
          <h2>Find an Ultrahuman Store</h2>
          <form
            action=""
            onSubmit={(e) => {
              e.preventDefault();
              // The search is now handled by the debounced effect above
              // This form submission just prevents page reload
            }}
          >
            <div style={{ position: 'relative' }}>
              <input
                className={styles.searchBarInput}
                name="search"
                placeholder="Search by location, city or store name."
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.form?.dispatchEvent(
                      new Event('submit', { cancelable: true, bubbles: true }),
                    );
                    if (window) {
                      window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                      });
                      e.currentTarget.blur();
                    }
                  }
                }}
              />
              {localSearchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setLocalSearchQuery('');
                    FGC.clearSearch();
                  }}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.6)',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </form>
          {
            // TODO: mux the user location & search based userlocation btn
          }
          {FGC.resultType !== 'userLocation' ? (
            <div
              className="location-btn-container"
              style={{ textAlign: 'center' }}
            >
              <UserLocationFlowButton />
            </div>
          ) : null}

          {FGC.loading && FGC.storesLoaded ? (
            <div className={styles.loadingScreen}>
              <span className={styles.spinnerLoader}></span>
            </div>
          ) : null}

          {!FGC.loading && FGC.storesLoaded ? (
            <div className={styles.searchResultsContainer}>
              {FGC.resultType === 'search' && FGC.searchQuery ? (
                <h5>Search results for &quot;{FGC.searchQuery}&quot;</h5>
              ) : null}
              {FGC.resultType === 'cluster' ? (
                <h5>
                  <button
                    className={styles.backBtn}
                    onClick={() => {
                      FGC.setSelectedCluster(null);
                      FGC.setResultType('search');
                    }}
                  >
                    <CaretUp style={{ transform: 'rotate(-90deg)' }} /> Back
                  </button>{' '}
                  {FGC.selectedCluster?.count} stores found
                </h5>
              ) : null}
              {
                // TODO: mux the user location & search based copies
              }
              {FGC.resultType === 'userLocation' &&
              FGC.searchResult.length > 0 ? (
                <h5>Stores nearest to your location</h5>
              ) : null}
              {getResultArray().length > 0 ? (
                getResultArray().map((store, index) => (
                  <SearchResultElement
                    store={store}
                    index={index}
                    key={index}
                    active={
                      FGC.selectedStore?.retailPartnerName ===
                      store.retailPartnerName
                    }
                  />
                ))
              ) : (
                <h5>No results found</h5>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const FinderControlsUI = () => {
  const FGC = useContext(FindOnGlobeContext);

  console.log(
    'FinderControlsUI render - screenState:',
    FGC.screenState,
    'resultType:',
    FGC.resultType,
    'searchResult length:',
    FGC.searchResult.length,
  );

  return (
    <section className={styles.finderRoot}>
      {FGC.screenState === ScreenState.HOME ? <LandingScreenContent /> : null}
      {FGC.screenState === ScreenState.SEARCH ? <SearchScreenContent /> : null}
    </section>
  );
};
